import {
  cloneElement,
  isValidElement,
  type CSSProperties,
  type ReactNode,
} from "react";
import MuiButton, {
  type ButtonProps as MuiButtonProps,
} from "@mui/material/Button";
import { styled } from "@mui/material/styles";

type NavigationButtonProps = MuiButtonProps &
  Pick<MuiButtonProps<"a">, "target" | "rel"> & {
    inactive?: boolean;
    children: ReactNode;
  };

const inheritIconFontSize = (icon: ReactNode) => {
  if (!isValidElement<{ style?: CSSProperties }>(icon)) {
    return icon;
  }

  return cloneElement(icon, {
    style: { ...icon.props.style, fontSize: "inherit" },
  });
};

const StyledNavigationButton = styled(MuiButton, {
  shouldForwardProp: (property) => property !== "inactive",
})<{ inactive: boolean }>(({ theme, inactive }) => {
  const { action } = theme.palette;
  const inactiveStyles = {
    borderColor: action.disabled,
    backgroundColor: action.disabled,
  };
  return {
    padding: "8px 20px",
    fontSize: "0.875rem",
    lineHeight: 1.25,
    fontWeight: 500,
    textTransform: "none",
    ...(inactive ? inactiveStyles : {}),
  };
});

export const NavigationButton = ({
  variant = "contained",
  inactive = false,
  children,
  startIcon,
  endIcon,
  ...props
}: NavigationButtonProps) => {
  return (
    <StyledNavigationButton
      variant={variant}
      inactive={inactive}
      startIcon={inheritIconFontSize(startIcon)}
      endIcon={inheritIconFontSize(endIcon)}
      {...props}
    >
      {children}
    </StyledNavigationButton>
  );
};
